// types & constants (ID-based)

export type Category =
  | "Shots"
  | "Distribution"
  | "Defensive"
  | "Possession"
  | "Discipline"
  | "Goalkeeping";

export const SUBTYPE = {
  // Shots
  Goals: 1,
  ShotOnTargets: 2,
  ShotOffTargets: 3,
  BlockedShots: 4,

  // Distribution
  SuccessfulPasses: 10,
  UnsuccessfulPasses: 11,
  SuccessfulCrosses: 12,
  UnsuccessfulCrosses: 13,
  KeyPasses: 14,
  Assists: 15,

  // Defensive
  Tackles: 20,
  Interceptions: 21,
  Clearances: 22,

  // Possession
  DribbleSuccess: 30,
  DribbleFail: 31,

  // Discipline
  Fouls: 40,
  Offsides: 42,

  // Goalkeeping
  Saves: 50,
  Claims: 51,
  Punches: 52,

  // Other
  Other: 99,
} as const;

export type SubTypeId = (typeof SUBTYPE)[keyof typeof SUBTYPE];

export const SUBTYPE_LABELS: Record<SubTypeId, string> = {
  [SUBTYPE.Goals]: "Goals",
  [SUBTYPE.ShotOnTargets]: "On Target",
  [SUBTYPE.ShotOffTargets]: "Off Target",
  [SUBTYPE.BlockedShots]: "Blocked",

  [SUBTYPE.SuccessfulPasses]: "Successful Passes",
  [SUBTYPE.UnsuccessfulPasses]: "Unsuccessful Passes",
  [SUBTYPE.SuccessfulCrosses]: "Successful Crosses",
  [SUBTYPE.UnsuccessfulCrosses]: "Unsuccessful Crosses",
  [SUBTYPE.KeyPasses]: "Key Passes",
  [SUBTYPE.Assists]: "Assists",

  [SUBTYPE.Tackles]: "Tackle",
  [SUBTYPE.Interceptions]: "Interception",
  [SUBTYPE.Clearances]: "Clearance",

  [SUBTYPE.DribbleSuccess]: "Dribble Success",
  [SUBTYPE.DribbleFail]: "Dribble Fail",

  [SUBTYPE.Fouls]: "Fouls Won",
  [SUBTYPE.Offsides]: "Offsides",

  [SUBTYPE.Saves]: "Saves",
  [SUBTYPE.Claims]: "Claims",
  [SUBTYPE.Punches]: "Punches",

  [SUBTYPE.Other]: "Other",
};

export interface ChalkboardEvent {
  id: string;
  team: "home" | "away";
  x: number; // 0..1
  y: number; // 0..1
  playerNum: string | number;
  category: Category;
  subtype: SubTypeId; // <-- ID!
  minute: number;
  playerName: string;
  teamName: string;
  /** Optional target point for arrows (e.g., off-target landing or block point) */
  tx?: number; // 0..1
  ty?: number; // 0..1
}

export const CATEGORY_SUBTYPES: Record<Category, SubTypeId[]> = {
  Shots: [
    SUBTYPE.Goals,
    SUBTYPE.ShotOnTargets,
    SUBTYPE.ShotOffTargets,
    SUBTYPE.BlockedShots,
  ],
  Distribution: [
    SUBTYPE.SuccessfulPasses,
    SUBTYPE.UnsuccessfulPasses,
    SUBTYPE.SuccessfulCrosses,
    SUBTYPE.UnsuccessfulCrosses,
    SUBTYPE.KeyPasses,
    SUBTYPE.Assists,
  ],
  Defensive: [SUBTYPE.Tackles, SUBTYPE.Interceptions, SUBTYPE.Clearances],
  Possession: [SUBTYPE.DribbleSuccess, SUBTYPE.DribbleFail],
  Discipline: [SUBTYPE.Fouls, SUBTYPE.Offsides],
  Goalkeeping: [SUBTYPE.Saves, SUBTYPE.Claims, SUBTYPE.Punches],
};

export function getShapeByCategory(category: Category): string {
  switch (category) {
    case "Shots":
      return "circle";
    case "Distribution":
      return "square";
    case "Defensive":
      return "triangle";
    case "Possession":
      return "diamond";
    case "Discipline":
      return "triangle-inverted";
    case "Goalkeeping":
      return "pentagon";
    default:
      return "circle";
  }
}
